import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL;
const getToken = () => localStorage.getItem('authToken');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

/**
 * SubmitSearchModal
 * 
 * Shown when a client clicks "Submit" on a draft search.
 * Lets them select one or more active search partners to assign.
 */
const SubmitSearchModal = ({ pipelineId, pipelineName, onClose, onSubmitted }) => {
  const [partners, setPartners] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API}/partnerships/partners`, { headers: headers() });
      setPartners(res.data);
      // Auto-select all by default
      setSelectedIds(res.data.map(p => p.firm_id));
    } catch (err) {
      console.error('Error loading partners:', err);
      setError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const togglePartner = (firmId) => {
    setSelectedIds(prev => 
      prev.includes(firmId) ? prev.filter(id => id !== firmId) : [...prev, firmId]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === partners.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(partners.map(p => p.firm_id));
    }
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      setError('Please select at least one search partner');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/pipelines/${pipelineId}/submit`, {
        partner_firm_ids: selectedIds
      }, { headers: headers() });
      
      if (onSubmitted) onSubmitted();
    } catch (err) {
      console.error('Error submitting search:', err);
      setError(err.response?.data?.error || 'Failed to submit search');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Submit Search</h2>
              <p className="text-sm text-gray-500 mt-0.5">{pipelineName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-3">Loading partners...</p>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-600 font-medium">No search partners yet</p>
              <p className="text-sm text-gray-400 mt-1">You need to add at least one recruitment partner before submitting a search.</p>
              <p className="text-sm text-gray-400 mt-2">Go to <span className="font-medium text-blue-600">Profile → Search Partners</span> to invite one.</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">Select the recruitment partners who will work on this search. They will be notified immediately.</p>
                
                {/* Select All */}
                <button onClick={toggleAll} className="text-xs text-blue-600 hover:text-blue-700 mb-2">
                  {selectedIds.length === partners.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              <div className="space-y-2">
                {partners.map((partner) => {
                  const isSelected = selectedIds.includes(partner.firm_id);
                  return (
                    <button
                      key={partner.firm_id}
                      onClick={() => togglePartner(partner.firm_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 ${
                        isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>

                      {/* Icon */}
                      <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4.5 h-4.5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {partner.firm_name}
                        </p>
                        <p className="text-xs text-gray-500">Recruitment Firm</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0 flex items-center justify-between bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-500">
            {partners.length > 0 ? `${selectedIds.length} of ${partners.length} partner${partners.length > 1 ? 's' : ''} selected` : ''}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-white">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || partners.length === 0 || selectedIds.length === 0}
              className={`px-5 py-2 text-sm rounded-lg font-medium ${
                submitting || partners.length === 0 || selectedIds.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  Submitting...
                </span>
              ) : 'Submit Search'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitSearchModal;
